import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { CHIP_SIZE, CHIP_TYPE, COMMENT_TYPE, ICON_SIZE, LABEL_COLOUR_HASH } from '../../../global/constants';
import Dropdown from '../../dropdown/mainDropdown';
import DropdownItem from '../../dropdown/dropdownItem';
import { createNewComment, updateRecurringTaskDetails, updateTaskDetails } from '../../../services/taskServices';
import { ColorIcon, Icon, IconDiv, IconMainDiv, LabelIcon, RightText, SelectedLabelDiv } from './styles';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { ItemDiv } from '../../customContextMenu/styles';
import { getRawComment, isEmpty } from '../../../helpers/common';
import { TaskModel } from '../../../mobx/models/task';
import { ProjectModel } from '../../../mobx/models/project';
import { rootStore } from '../../../mobx/rootStore';
import { Label } from '../../../mobx/interfaces/project';
import { nanoid } from 'nanoid';
import { ProjectTemplateModel } from '../../../mobx/models/projectTemplate';
import Chip from '../../chipComponent';
import CheckboxInput from '../../input/checkboxInput';
import { observer } from 'mobx-react-lite';

/**
 * Interface defining the props for the LabelDropdownV2 component
 * @interface Props
 * @property {TaskModel} taskData - Optional task data model
 * @property {ProjectModel} currentProject - Current project model
 * @property {boolean} isCreateTask - Flag indicating if this is for task creation
 * @property {boolean} isFromTaskDetails - Flag indicating if rendered from task details
 * @property {boolean} isContextMenu - Flag indicating if this is a context menu
 * @property {boolean} isRecurringTask - Flag indicating if the task is a recurring task
 */
interface Props {
  taskData?: TaskModel;
  currentProject: ProjectModel | ProjectTemplateModel;
  isCreateTask?: boolean;
  isFromTaskDetails?: boolean;
  isContextMenu?: boolean;
  isTemplateProject?: boolean;
  isRecurringTask?: boolean;
}

// observer() so the selected-label display reacts to in-place label mutations on the task.
// Under the merge upsert strategy the task instance is mutated in place, so this component
// must observe taskData.labels to repaint the chips without an unrelated re-render.
const LabelDropdown: React.FC<Props> = observer((props) => {
  //Props declaration
  const {
    taskData,
    isCreateTask,
    isContextMenu,
    isFromTaskDetails,
    currentProject,
    isTemplateProject = false,
    isRecurringTask = false
  } = props;

  // ref variables
  const selectedLabelRef = useRef<HTMLDivElement>(null);
  //use selector state variables
  const [searchValue, setSearchValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  //Mobx store variables
  const { taskStore, currentUser, projectTemplateStore } = rootStore;
  const { createTaskInput } = taskStore;
  const { currentProjectTemplate } = projectTemplateStore;
  const labelData = isCreateTask ? createTaskInput?.labels : taskData?.labels;

  // Derive the visible label list from the active project's labels and the search query.
  // Reading currentProject.labelsList directly (under observer) keeps it reactive, so the
  // list stays correct when switching projects without mirroring the observable into state.
  const searchResult = useMemo(() => {
    const labels = currentProject.labelsList || [];
    if (isEmpty(searchValue)) return labels;
    return labels.filter((item: Label) => item?.name?.toLowerCase().includes(searchValue.toLowerCase()));
  }, [currentProject.labelsList, searchValue]);

  // useEffect to check the height of the selected label div and add/remove the multiline class to the icon div
  useEffect(() => {
    if (selectedLabelRef.current) {
      const height = selectedLabelRef.current.getBoundingClientRect().height;

      // apply css to the icon div based on the height of the selected label div
      const iconDiv = selectedLabelRef.current.closest('.icon-div');
      if (iconDiv) {
        if (height > 22) {
          iconDiv.classList.add('multiline');
        } else {
          iconDiv.classList.remove('multiline');
        }
      }
    }
  }, [labelData]); // Re-run when labelData changes

  /**
   * Handles the click event on a label
   * @param {Label} item - The label item that was clicked
   */
  const onClickLabel = useCallback(
    async (item: Label) => {
      if (isCreateTask) {
        // Handle label selection for create task input
        if (createTaskInput?.hasLabel(item.id)) {
          createTaskInput.removeLabel(item.id);
        } else {
          createTaskInput.addLabel(item.id);
        }
      } else {
        // Handle label selection for task details
        if (!currentUser) return;

        let newComment = {
          id: nanoid(),
          type: COMMENT_TYPE.ADD_LABEL,
          group: item?.name
        };
        const handleLabelChange = async () => {
          if (taskData?.hasLabel(item.id)) {
            taskData?.removeLabel(item.id);
            newComment.type = COMMENT_TYPE.REMOVE_LABEL;
          } else {
            taskData?.addLabel(item.id);
          }
        };

        if (isRecurringTask) {
          // Recurring tasks use a dedicated endpoint and do not track comments
          handleLabelChange();
          const payloadTask = { labels: taskData?.labelsIds, projectId: taskData?.projectId };
          await updateRecurringTaskDetails(taskData?.id, payloadTask);
        } else if (!isTemplateProject) {
          handleLabelChange();
          // Update comment in mobx store
          const updatedComment = getRawComment(newComment, currentUser);
          taskData?.upsertComment(updatedComment);

          // Send API request to update the task details and add a new comment
          const payloadTask = { labels: taskData?.labelsIds, projectId: taskData?.projectId };
          await updateTaskDetails(taskData?.id, payloadTask);
          await createNewComment(taskData?.id, {
            Type: newComment.type,
            Group: item?.name
          });
        } else {
          if (currentProjectTemplate) {
            const updatedTaskList = currentProjectTemplate.tasks.map((item) => {
              if (item?.id === taskData?.id) {
                handleLabelChange();
              }
              return item;
            });
            rootStore.projectTemplateStore.currentProjectTemplate?.setTasks(updatedTaskList || []);
          }
        }
      }
    },
    [isCreateTask, createTaskInput, currentUser, taskData, isTemplateProject, isRecurringTask, currentProjectTemplate]
  );

  // Renders the list of labels with checkboxes
  const renderLabelList = useCallback(() => {
    const labelData = isCreateTask ? createTaskInput?.labelsIds : taskData?.labelsIds;
    return searchResult.map((item) => {
      // Check if the current label is selected
      const isChecked = labelData?.some((labelId) => labelId === item.id);
      return (
        <DropdownItem
          key={item?.name}
          label={item?.name}
          isSelected={isChecked}
          onClick={(e) => {
            e?.stopPropagation();
            onClickLabel(item);
          }}>
          <CheckboxInput
            checked={isChecked}
            onClick={(e) => {
              e.stopPropagation();
              onClickLabel(item);
            }}
            checkboxType='square-checkbox'
          />
          <LabelIcon color={`${LABEL_COLOUR_HASH[item?.color]}`} />
        </DropdownItem>
      );
    });
  }, [isCreateTask, createTaskInput?.labelsIds, taskData, searchResult, onClickLabel]);

  // Renders the selected labels display
  const renderSelectedLabel = useMemo(() => {
    const labelData = isCreateTask ? createTaskInput?.labels : taskData?.labels;
    return labelData?.map((item) => (
      <ColorIcon key={item?.id}>
        <IconMainDiv>
          <LabelIcon color={`${LABEL_COLOUR_HASH[item?.color]}`} />
        </IconMainDiv>
        <RightText>{item?.name}</RightText>
      </ColorIcon>
    ));
  }, [createTaskInput?.labels, isCreateTask, taskData?.labels]);

  /**
   * Handles search input changes for filtering labels
   * @param {string} value - Search input value
   */
  const onChangeSearch = useCallback((value: string) => {
    // Store the query; the visible list is derived from it in the searchResult memo.
    setSearchValue(value);
  }, []);

  return (
    <Dropdown
      isMinWidth={148}
      onChangeSearch={onChangeSearch}
      content={
        isContextMenu ? (
          <ItemDiv>
            <DropdownItem
              label='Label'
              iconName='updated-label-icon'
              iconSize={16}
              iconViewBox='0 0 18 18'
              iconTone='fill'
            />
          </ItemDiv>
        ) : labelData && labelData?.length > 0 ? (
          <IconDiv
            isCreateTask={isCreateTask}
            isTaskDetail={isFromTaskDetails}
            className='icon-div'
            isActive={isDropdownOpen}>
            <Icon>
              <SVGIcon
                name='updated-label-icon'
                width={ICON_SIZE.MD}
                height={ICON_SIZE.MD}
                viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                fill='var(--text-secondary)'
                className='label-icon'
              />
            </Icon>
            <SelectedLabelDiv ref={selectedLabelRef}>{renderSelectedLabel}</SelectedLabelDiv>
          </IconDiv>
        ) : (
          <Chip
            title={!isCreateTask ? 'Add Label' : ''}
            size={isCreateTask ? CHIP_SIZE.SMALL : CHIP_SIZE.MEDIUM}
            variant={isCreateTask ? CHIP_TYPE.FILL : CHIP_TYPE.EMPTY}
            leadingIconName='updated-label-icon'
            isActive={isDropdownOpen}
          />
        )
      }
      modalTitle='Labels'
      trigger={isContextMenu ? 'hover' : 'click'}
      updateDropdownOpen={(isOpen) => setIsDropdownOpen(isOpen)}>
      {renderLabelList()}
    </Dropdown>
  );
});

export default LabelDropdown;
